import { useState, useEffect } from "react";
import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
} from "firebase/firestore";
import { db, auth } from "../firebase";
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential,
  sendPasswordResetEmail,
  setPersistence,
  browserSessionPersistence,
} from "firebase/auth";
import "../style.css";

/* =========================
   Cloudinary Upload Helper
========================= */
async function uploadToCloudinary(file) {
  const cloudName = "djn8tgqam";   // ✅ your cloud name
  const uploadPreset = "djn8tgqam"; // ✅ your preset name

  const formData = new FormData();
  formData.append("file", file);
  formData.append("upload_preset", uploadPreset);

  const res = await fetch(
    `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`,
    {
      method: "POST",
      body: formData,
    }
  );

  const data = await res.json();
  if (!res.ok) throw new Error("Upload failed");

  return data.secure_url;
}

const MAX_PRODUCT_IMAGES = 3;
const ALLOWED_ADMINS = ["firebaseone342@gmail.com"];

const isAllowedAdminEmail = (email = "") =>
  ALLOWED_ADMINS.some((allowed) => allowed.toLowerCase() === email.toLowerCase());

const getLaptopImages = (laptop = {}) => {
  const arrayImages = Array.isArray(laptop.images) ? laptop.images : [];
  const galleryImages = Array.isArray(laptop.gallery) ? laptop.gallery : [];
  const legacyImages = [laptop.image, laptop.image1, laptop.image2, laptop.image3];

  const merged = [...arrayImages, ...galleryImages, ...legacyImages]
    .map((image) => String(image || "").trim())
    .filter(Boolean);

  return [...new Set(merged)].slice(0, MAX_PRODUCT_IMAGES);
};

function Admin() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [authError, setAuthError] = useState("");
  const [authInfo, setAuthInfo] = useState("");
  const [adminDataError, setAdminDataError] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [laptops, setLaptops] = useState([]);
  const [sliders, setSliders] = useState([]);
  
  // Tab state
  const [activeTab, setActiveTab] = useState("laptops");

  // Laptop form state
  const [name, setName] = useState("");
  const [price, setPrice] = useState("");
  const [discountPrice, setDiscountPrice] = useState("");
  const [specs, setSpecs] = useState("");
  const [imageFiles, setImageFiles] = useState(
    Array(MAX_PRODUCT_IMAGES).fill(null)
  );
  const [imagePreviews, setImagePreviews] = useState(
    Array(MAX_PRODUCT_IMAGES).fill("")
  );
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  // Slider form state
  const [sliderFile, setSliderFile] = useState(null);
  const [sliderPreview, setSliderPreview] = useState("");
  const [sliderTitle, setSliderTitle] = useState("");

  // Change password state
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  /* =========================
     Load laptops from Firestore
  ========================= */
  const loadLaptops = async () => {
    try {
      const snap = await getDocs(collection(db, "laptops"));
      const data = snap.docs.map((d) => ({ _id: d.id, ...d.data() }));
      setLaptops(data);
      setAdminDataError("");
    } catch (error) {
      console.error("Failed to load laptops", error);
      setAdminDataError("Failed to load admin data. Please check your connection and try again.");
    }
  };

  /* =========================
     Load sliders from Firestore
  ========================= */
  const loadSliders = async () => {
    try {
      const snap = await getDocs(collection(db, "sliders"));
      const data = snap.docs.map((d) => ({ _id: d.id, ...d.data() }));
      // Sort by order or date if available
      data.sort((a, b) => (b.order || 0) - (a.order || 0));
      setSliders(data);
      setAdminDataError("");
    } catch (error) {
      console.error("Failed to load sliders", error);
      setAdminDataError("Failed to load admin data. Please check your connection and try again.");
    }
  };

  useEffect(() => {
    setPersistence(auth, browserSessionPersistence).catch((err) => {
      console.error("Failed to set auth persistence", err);
    });
    loadLaptops();
    loadSliders();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        const userEmail = (user.email || "").toLowerCase();
        const isAllowed = isAllowedAdminEmail(userEmail);
        if (!isAllowed) {
          setAuthError("This account is not authorized for admin access.");
          signOut(auth);
          setIsLoggedIn(false);
        } else {
          setIsLoggedIn(true);
        }
      } else {
        setIsLoggedIn(false);
      }
      setAuthLoading(false);
    });
    return () => unsubscribe();
  }, []);

  /* =========================
     Login
  ========================= */
  const handleLogin = async () => {
    if (!email || !password) {
      setAuthError("Enter email and password");
      setAuthInfo("");
      return;
    }
    const emailLower = email.toLowerCase();
    const isAllowed = isAllowedAdminEmail(emailLower);
    if (!isAllowed) {
      setAuthError("This account is not authorized for admin access.");
      setAuthInfo("");
      return;
    }
    setAuthError("");
    setAuthInfo("");
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err) {
      console.error(err);
      setAuthError("Invalid email or password");
    }
  };

  const handleLogout = async () => {
    await signOut(auth);
  };

  const handleResetPassword = async () => {
    if (!email) {
      setAuthError("Enter your admin email to reset password");
      setAuthInfo("");
      return;
    }
    const emailLower = email.toLowerCase();
    const isAllowed = isAllowedAdminEmail(emailLower);
    if (!isAllowed) {
      setAuthError("This account is not authorized for admin access.");
      setAuthInfo("");
      return;
    }
    setAuthError("");
    try {
      await sendPasswordResetEmail(auth, email);
      setAuthInfo("Password reset link sent to your email.");
    } catch (err) {
      console.error(err);
      const code = err?.code ? ` (${err.code})` : "";
      setAuthError(`Failed to send reset email${code}`);
      setAuthInfo("");
    }
  };

  /* =========================
     File preview - Laptop
  ========================= */
  const handleLaptopImageChange = (index, e) => {
    const selectedFile = e.target.files?.[0] || null;

    setImageFiles((previous) => {
      const next = [...previous];
      next[index] = selectedFile;
      return next;
    });

    setImagePreviews((previous) => {
      const next = [...previous];
      next[index] = selectedFile ? URL.createObjectURL(selectedFile) : "";
      return next;
    });
  };

  const clearLaptopImage = (index) => {
    setImageFiles((previous) => {
      const next = [...previous];
      next[index] = null;
      return next;
    });

    setImagePreviews((previous) => {
      const next = [...previous];
      next[index] = "";
      return next;
    });
  };

  /* =========================
     File preview - Slider
  ========================= */
  const handleSliderFileChange = (e) => {
    const f = e.target.files[0];
    setSliderFile(f);
    if (f) setSliderPreview(URL.createObjectURL(f));
  };

  /* =========================
     Add / Update laptop
  ========================= */
  const saveLaptop = async () => {
    try {
      if (!name || !price || !specs) return alert("Fill all fields");

      const hasAnyImage =
        imageFiles.some(Boolean) || imagePreviews.some((img) => Boolean(img));
      if (!editId && !hasAnyImage) return alert("Choose at least one image");

      setSaving(true);

      const nextImages = [...imagePreviews];
      for (let i = 0; i < MAX_PRODUCT_IMAGES; i += 1) {
        if (imageFiles[i]) {
          nextImages[i] = await uploadToCloudinary(imageFiles[i]);
        }
      }

      const images = nextImages
        .map((image) => String(image || "").trim())
        .filter(Boolean)
        .slice(0, MAX_PRODUCT_IMAGES);

      if (!images.length) return alert("Choose at least one image");

      const laptopData = {
        name,
        price,
        discountPrice,
        specs,
        image1: images[0],
        image: images[0],
        image2: images[1] || "",
        image3: images[2] || "",
        images,
      };

      if (editId) {
        await updateDoc(doc(db, "laptops", editId), laptopData);
      } else {
        await addDoc(collection(db, "laptops"), laptopData);
      }

      alert("✅ Laptop saved!");
      resetLaptopForm();
      loadLaptops();
    } catch (err) {
      console.error(err);
      alert("Upload failed");
    } finally {
      setSaving(false);
    }
  };

  /* =========================
     Reset laptop form
  ========================= */
  const resetLaptopForm = () => {
    setName("");
    setPrice("");
    setDiscountPrice("");
    setSpecs("");
    setImageFiles(Array(MAX_PRODUCT_IMAGES).fill(null));
    setImagePreviews(Array(MAX_PRODUCT_IMAGES).fill(""));
    setEditId(null);
  };

  /* =========================
     Reset slider form
  ========================= */
  const resetSliderForm = () => {
    setSliderFile(null);
    setSliderPreview("");
    setSliderTitle("");
  };

  /* =========================
     Add Slider Image
  ========================= */
  const saveSlider = async () => {
    try {
      if (!sliderFile && !sliderPreview) return alert("Choose an image");

      setSaving(true);

      let imageURL = sliderPreview;

      if (sliderFile) {
        imageURL = await uploadToCloudinary(sliderFile);
      }

      const sliderData = {
        image: imageURL,
        title: sliderTitle,
        order: sliders.length + 1,
        createdAt: new Date().toISOString()
      };

      await addDoc(collection(db, "sliders"), sliderData);

      alert("✅ Slider image added!");
      resetSliderForm();
      loadSliders();
    } catch (err) {
      console.error(err);
      alert("Upload failed");
    } finally {
      setSaving(false);
    }
  };

  /* =========================
     Delete Slider
  ========================= */
  const deleteSlider = async (id) => {
    if (window.confirm("Are you sure you want to delete this slider?")) {
      try {
        await deleteDoc(doc(db, "sliders", id));
        loadSliders();
      } catch (error) {
        console.error("Failed to delete slider", error);
        setAdminDataError("Failed to delete slider. Please try again.");
      }
    }
  };

  /* =========================
     Edit
  ========================= */
  const editLaptop = (l) => {
    setEditId(l._id);
    setName(l.name);
    setPrice(l.price);
    setDiscountPrice(l.discountPrice || "");
    setSpecs(l.specs);
    const currentImages = getLaptopImages(l);
    setImagePreviews(
      Array.from({ length: MAX_PRODUCT_IMAGES }, (_, index) => currentImages[index] || "")
    );
    setImageFiles(Array(MAX_PRODUCT_IMAGES).fill(null));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  /* =========================
     Delete
  ========================= */
  const deleteLaptop = async (id) => {
    try {
      await deleteDoc(doc(db, "laptops", id));
      loadLaptops();
    } catch (error) {
      console.error("Failed to delete laptop", error);
      setAdminDataError("Failed to delete laptop. Please try again.");
    }
  };

  /* =========================
     Login Screen
  ========================= */
  if (authLoading) {
    return (
      <div className="admin-login">
        <h2>Loading...</h2>
      </div>
    );
  }

  if (!isLoggedIn) {
    return (
      <div className="admin-login">
        <h2>Admin Login</h2>
        <input
          type="email"
          placeholder="Admin Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <div className="admin-password-field">
          <input
            type={showLoginPassword ? "text" : "password"}
            placeholder="Admin Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <button
            type="button"
            className="admin-eye-btn"
            onClick={() => setShowLoginPassword((v) => !v)}
            aria-label={showLoginPassword ? "Hide password" : "Show password"}
          >
            {showLoginPassword ? "Hide" : "Show"}
          </button>
        </div>
        {authError && <p className="admin-error">{authError}</p>}
        {authInfo && <p className="admin-info">{authInfo}</p>}
        <button onClick={handleLogin}>Login</button>
        <button
          type="button"
          className="admin-link"
          onClick={handleResetPassword}
        >
          Forgot password?
        </button>
      </div>
    );
  }

  /* =========================
     Admin UI
  ========================= */
  return (
    <div className="admin">
      <div className="admin-header">
        <h2>Admin Panel</h2>
        <button onClick={handleLogout} className="danger">Logout</button>
      </div>

      {adminDataError && (
        <div className="admin-error admin-error-banner">
          {adminDataError}
          <button
            type="button"
            className="admin-link"
            onClick={() => {
              setAdminDataError("");
              loadLaptops();
              loadSliders();
            }}
          >
            Retry
          </button>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="admin-tabs">
        <button 
          className={activeTab === "laptops" ? "active" : ""} 
          onClick={() => setActiveTab("laptops")}
        >
          Laptops
        </button>
        <button 
          className={activeTab === "sliders" ? "active" : ""} 
          onClick={() => setActiveTab("sliders")}
        >
          Slider Images
        </button>
      </div>

      {/* Laptop Management Tab */}
      {activeTab === "laptops" && (
        <>
          <h3>{editId ? "Edit Laptop" : "Add Used & Refurbished Laptop"}</h3>
          
          <input
            placeholder="Laptop Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />

          <input
            type="number"
            placeholder="Price"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
          />

          <input
            type="number"
            placeholder="Discount Price (optional)"
            value={discountPrice}
            onChange={(e) => setDiscountPrice(e.target.value)}
          />

          <textarea
            placeholder="Specs"
            value={specs}
            onChange={(e) => setSpecs(e.target.value)}
          />

          <div className="admin-product-images">
            {Array.from({ length: MAX_PRODUCT_IMAGES }).map((_, index) => (
              <div key={index} className="admin-product-image-field">
                <label className="admin-product-image-label">
                  Product Image {index + 1}
                  {index === 0 ? " *" : ""}
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleLaptopImageChange(index, e)}
                />
                {imagePreviews[index] && (
                  <>
                    <img
                      src={imagePreviews[index]}
                      className="admin-product-preview"
                      alt={`product preview ${index + 1}`}
                    />
                    <button
                      type="button"
                      className="admin-remove-image"
                      onClick={() => clearLaptopImage(index)}
                    >
                      Remove
                    </button>
                  </>
                )}
              </div>
            ))}
          </div>

          <button onClick={saveLaptop} disabled={saving}>
            {saving ? "Saving..." : editId ? "Update Laptop" : "Add Laptop"}
          </button>

          {editId && <button onClick={resetLaptopForm}>Cancel Edit</button>}

          <hr />

          <h3>Existing Laptops</h3>
          <div className="grid">
            {laptops.map((l) => (
              <div key={l._id} className="card">
                <img
                  src={getLaptopImages(l)[0] || "placeholder.png"}
                  width="200"
                  alt=""
                  onError={(e) => (e.currentTarget.src = "placeholder.png")}
                />
                <h3>{l.name}</h3>
                <p>{l.specs}</p>
                {l.discountPrice ? (
                  <h4>
                    ₹{l.discountPrice} <s>₹{l.price}</s>
                  </h4>
                ) : (
                  <h4>₹{l.price}</h4>
                )}

                <button onClick={() => editLaptop(l)}>Edit</button>
                <button onClick={() => deleteLaptop(l._id)} className="danger">Delete</button>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Slider Management Tab */}
      {activeTab === "sliders" && (
        <>
          <h3>Add Slider Image</h3>
          
          <input
            type="text"
            placeholder="Slider Title (optional)"
            value={sliderTitle}
            onChange={(e) => setSliderTitle(e.target.value)}
          />

          <input type="file" accept="image/*" onChange={handleSliderFileChange} />

          {sliderPreview && (
            <img
              src={sliderPreview}
              className="admin-slider-preview"
              alt="slider preview"
            />
          )}

          <button onClick={saveSlider} disabled={saving}>
            {saving ? "Saving..." : "Add Slider Image"}
          </button>

          <hr />

          <h3>Existing Slider Images</h3>
          <div className="slider-grid">
            {sliders.map((s) => (
              <div key={s._id} className="slider-card">
                <img src={s.image} alt={s.title} />
                <p>{s.title}</p>
                <button onClick={() => deleteSlider(s._id)} className="danger">Delete</button>
              </div>
            ))}
          </div>
          {sliders.length === 0 && <p>No slider images added yet.</p>}
        </>
      )}

      {/* Change Password */}
      <div className="admin-change-password">
        <h3>Change Password</h3>
        <div className="admin-password-field">
          <input
            type={showCurrentPassword ? "text" : "password"}
            placeholder="Current Password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
          />
          <button
            type="button"
            className="admin-eye-btn"
            onClick={() => setShowCurrentPassword((v) => !v)}
            aria-label={showCurrentPassword ? "Hide password" : "Show password"}
          >
            {showCurrentPassword ? "Hide" : "Show"}
          </button>
        </div>
        <div className="admin-password-field">
          <input
            type={showNewPassword ? "text" : "password"}
            placeholder="New Password (min 6 chars)"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
          <button
            type="button"
            className="admin-eye-btn"
            onClick={() => setShowNewPassword((v) => !v)}
            aria-label={showNewPassword ? "Hide password" : "Show password"}
          >
            {showNewPassword ? "Hide" : "Show"}
          </button>
        </div>
        <div className="admin-password-field">
          <input
            type={showConfirmPassword ? "text" : "password"}
            placeholder="Confirm New Password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
          <button
            type="button"
            className="admin-eye-btn"
            onClick={() => setShowConfirmPassword((v) => !v)}
            aria-label={showConfirmPassword ? "Hide password" : "Show password"}
          >
            {showConfirmPassword ? "Hide" : "Show"}
          </button>
        </div>
        <button
          onClick={async () => {
            if (!auth.currentUser) return;
            if (!currentPassword || !newPassword || !confirmPassword) {
              return alert("Fill all password fields");
            }
            if (newPassword.length < 6) {
              return alert("New password must be at least 6 characters");
            }
            if (newPassword !== confirmPassword) {
              return alert("New passwords do not match");
            }
            try {
              setChangingPassword(true);
              const credential = EmailAuthProvider.credential(
                auth.currentUser.email || "",
                currentPassword
              );
              await reauthenticateWithCredential(auth.currentUser, credential);
              await updatePassword(auth.currentUser, newPassword);
              setCurrentPassword("");
              setNewPassword("");
              setConfirmPassword("");
              alert("Password updated successfully");
            } catch (err) {
              console.error(err);
              alert("Failed to update password");
            } finally {
              setChangingPassword(false);
            }
          }}
          disabled={changingPassword}
        >
          {changingPassword ? "Updating..." : "Update Password"}
        </button>
      </div>
    </div>
  );
}

export default Admin;
